import {
  toCamelCase,
  toPascalCase,
  toSnakeCase
} from '@/modules/globalModule/components/helperMethods';
import { NodeGenStrategy } from './nodeGenStrategy';
import JSZip from 'jszip';
import {
  getMsgDependency,
  getNodeServiceDependencies,
  getTopicDependency,
  MsgDependencyInfo,
  ServiceDependencyInfo
} from './dependenciesGetter';

/**
 * Code generating strategy for turning nodes into python code
 */
export class PyNodeGenStrategy extends NodeGenStrategy {
  ////////////////////////////////////////////////////////////////////
  //                        Code Generation                         //
  ////////////////////////////////////////////////////////////////////
  /**
   * Generates the code file(s) required for the node, and places them in
   * the correct folders of the zip file
   * @param {*} zip
   */
  generateCodeZip(zip: JSZip): void {
    zip.file(`${toCamelCase(this.node.name)}.py`, this.getPythonCodeString());
  }

  getPythonCodeString() {
    return `#!/usr/bin/env python3
# Note: the file will only save edits in the designated locations
# editable locations are denoted by comments. DO NOT remove the
# generated comments
import rclpy
from rclpy.node import Node
${this.getMsgImports()}
${this.getSrvFileImports()}
# add custom imports below
${this.node.customText.importText}
# end custom imports

class ${toPascalCase(this.node.name)}(Node):

    def __init__(self):
        super().__init__('${toSnakeCase(this.node.name)}')
${this.getPublisherInitialisations()}${this.getSubscriberInitialisations()}${this.getServerInitialisations()}${this.getClientInitialisations()}
        # customize init function below
${this.node.customText.constructorText}
        # end init customization

${this.getSubscriberCallbacks()}${this.getServerCallbacks()}


    # add custom methods to the node below
${this.node.customText.methodsText}
    # end custom methods

def main(args=None):
    # customize main function below
${
  this.node.customText.mainText === ''
    ? `    # Note: editing any of the generated code here could break the node.
    # take precaution when editing
    rclpy.init(args=args)

    ${toSnakeCase(this.node.name)} = ${toPascalCase(this.node.name)}()

    rclpy.spin(${toSnakeCase(this.node.name)})

    ${toSnakeCase(this.node.name)}.destroy_node()
    rclpy.shutdown()`
    : this.node.customText.mainText
}
    # end main customization


if __name__ == '__main__':
    main()
`;
  }
  /**
   * returns a string containing the msg imports. there will be only
   * one import per package, and each msg type used within that package
   * will be seperated by commas. Includes all msg types used in publishers
   * and subscribers
   */
  getMsgImports(): any {
    const msgInfoArray: Array<MsgDependencyInfo> = [];
    [...new Set(this.node.subscribers.concat(this.node.publishers))].forEach(
      (topicId) => {
        let topicInfo = getTopicDependency(this.workspaceId, topicId);
        if (!topicInfo) return;
        let msgInfo = getMsgDependency(
          this.workspaceId,
          topicInfo.topic.msgType
        );
        if (msgInfo && !msgInfoArray.includes(msgInfo))
          msgInfoArray.push(msgInfo);
      }
    );

    return (
      msgInfoArray
        // order messages by package
        .reduce(
          (
            accumulator: MsgDependencyInfo[][],
            currMsgInfo: MsgDependencyInfo
          ) => {
            for (let entry of accumulator) {
              if (entry[0].packageId === currMsgInfo.packageId) {
                entry.push(currMsgInfo);
                return accumulator;
              }
            }
            accumulator.push([currMsgInfo]);
            return accumulator;
          },
          []
        )
        // format import strings
        .reduce((accumulator: string, currMsgs: MsgDependencyInfo[]) => {
          if (currMsgs.length === 0) return accumulator;
          accumulator += `from ${toSnakeCase(
            currMsgs[0].packageName
          )}.msg import ${currMsgs.length > 1 ? '( ' : ''}`;
          currMsgs.forEach((subMsgInfo, index) => {
            accumulator += `${subMsgInfo.msg.name}${
              index !== currMsgs.length - 1 ? ', ' : ''
            }`;
          });
          accumulator += `${currMsgs.length > 1 ? ')' : ''}\n`;
          return accumulator;
        }, '')
    );
  }

  /**
   * returns the string containing the publisher creation statements, for
   * use within the __init__ method of the nodes python file
   */
  getPublisherInitialisations(): string {
    return this.node.publishers.reduce((accumulator: string, publisherId) => {
      const topicInfo = getTopicDependency(this.workspaceId, publisherId);
      if (!topicInfo) return accumulator;
      const msgInfo = getMsgDependency(
        this.workspaceId,
        topicInfo.topic.msgType
      );
      if (!msgInfo) return accumulator;
      return (
        accumulator +
        `        self.${toSnakeCase(
          topicInfo.topic.name
        )}_pub = self.create_publisher(${
          msgInfo.msg.name
        }, '/${topicInfo.topic.getNameNoFirstSlash()}', 10)\n`
      );
    }, '');
  }
  /**
   * Returns a string containing the subscriber creation statements, for
   * use within the __init__ method of the nodes python file. links each subscriber
   * to a callback function called [subscriber name in snake case]_callback
   */
  getSubscriberInitialisations(): string {
    return this.node.subscribers.reduce((accumulator: string, subscriberId) => {
      const topicInfo = getTopicDependency(this.workspaceId, subscriberId);
      if (!topicInfo) return accumulator;
      const msgInfo = getMsgDependency(
        this.workspaceId,
        topicInfo.topic.msgType
      );
      if (!msgInfo) return accumulator;
      return (
        accumulator +
        `        self.${toSnakeCase(
          topicInfo.topic.name
        )}_sub = self.create_subscription(${
          msgInfo.msg.name
        }, '/${topicInfo.topic.getNameNoFirstSlash()}', self.${toSnakeCase(
          topicInfo.topic.name
        )}_callback, 10)\n`
      );
    }, '');
  }
  /**
   * Returns a string containing the python definitions for the callback
   * functions for each subscriber. each callback contains just a pass statement
   */
  getSubscriberCallbacks(): string {
    return this.node.subscribers.reduce((accumulator: string, subscriberId) => {
      const topicInfo = getTopicDependency(this.workspaceId, subscriberId);
      if (!topicInfo) return accumulator;
      const callbackInfo = this.node.customText.callbacks.find(
        (callbackInfo) =>
          callbackInfo.name === `${toSnakeCase(topicInfo.topic.name)}_callback`
      );
      const callbackText =
        (callbackInfo && callbackInfo.text) || '        pass';
      return (
        accumulator +
        `    def ${toSnakeCase(topicInfo.topic.name)}_callback(self, msg):
        # add ${toSnakeCase(topicInfo.topic.name)}_callback implementation below
${callbackText}
        # end ${toSnakeCase(
          topicInfo.topic.name
        )}_callback implementation\n    \n`
      );
    }, '');
  }

  ////////////////////////////////////////////////////////////////////
  //                 Service and Server Functions                   //
  ////////////////////////////////////////////////////////////////////
  getSrvFileImports(): string {
    let nodeSrvDependencies = getNodeServiceDependencies(
      this.workspaceId,
      this.node.id
    );

    // formatting import string
    return [
      ...new Set(
        nodeSrvDependencies.clients.concat(nodeSrvDependencies.servers)
      )
    ]
      .reduce(
        (
          accumulator: ServiceDependencyInfo[][],
          currSrvInfo: ServiceDependencyInfo
        ) => {
          for (let entry of accumulator) {
            if (entry[0].packageId === currSrvInfo.packageId) {
              entry.push(currSrvInfo);
              return accumulator;
            }
          }
          accumulator.push([currSrvInfo]);
          return accumulator;
        },
        []
      )
      .reduce((accumulator: string, currSrvs: ServiceDependencyInfo[]) => {
        if (currSrvs.length === 0) return accumulator;
        accumulator += `from ${toSnakeCase(
          currSrvs[0].packageName
        )}.srv import ${currSrvs.length > 1 ? '( ' : ''}`;
        currSrvs.forEach((subMsgInfo, index) => {
          accumulator += `${toCamelCase(subMsgInfo.srv.name)}${
            index !== currSrvs.length - 1 ? ', ' : ''
          }`;
        });
        accumulator += `${currSrvs.length > 1 ? ')' : ''}\n`;
        return accumulator;
      }, '');
  }

  getServerInitialisations(): string {
    let serverInitText: string = '';
    getNodeServiceDependencies(this.workspaceId, this.node.id).servers.forEach(
      (srvInfo: ServiceDependencyInfo) => {
        serverInitText += `        self.${toSnakeCase(
          srvInfo.srv.name
        )}_server = self.create_service(${toCamelCase(
          srvInfo.srv.name
        )}, '${toSnakeCase(srvInfo.srv.name)}', self.${toSnakeCase(
          srvInfo.srv.name
        )}_srv_callback)\n`;
      }
    );
    return serverInitText;
  }

  getClientInitialisations(): string {
    let clientInitText: string = '';
    getNodeServiceDependencies(this.workspaceId, this.node.id).clients.forEach(
      (srvInfo: ServiceDependencyInfo) => {
        clientInitText += `        self.${toSnakeCase(
          srvInfo.srv.name
        )}_client = self.create_client(${toCamelCase(
          srvInfo.srv.name
        )}, '${toSnakeCase(srvInfo.srv.name)}')
        while not self.${toSnakeCase(
          srvInfo.srv.name
        )}_client.wait_for_service(timeout_sec=1.0):
            self.get_logger().info('${toCamelCase(
              srvInfo.srv.name
            )} Service not available, waiting again...')\n`;
      }
    );
    return clientInitText;
  }

  getServerCallbacks(): string {
    let callbacksText: string = '';
    getNodeServiceDependencies(this.workspaceId, this.node.id).servers.forEach(
      (srvInfo: ServiceDependencyInfo) => {
        let callbackInfo = this.node.customText.callbacks.find(
          (customCallbackInfo) =>
            customCallbackInfo.name ===
            `${toSnakeCase(srvInfo.srv.name)}_srv_callback`
        );
        let customCallbackText =
          (callbackInfo && callbackInfo.text) || '        return response\n';
        callbacksText += `    def ${toSnakeCase(
          srvInfo.srv.name
        )}_srv_callback(self, request, response):
        # add ${toSnakeCase(srvInfo.srv.name)}_srv_callback implementation below
${customCallbackText}
        # end ${toSnakeCase(srvInfo.srv.name)}_srv_callback implementation\n`;
      }
    );
    return callbacksText;
  }
}
